import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageSquare, Users, Clock, TrendingUp, Settings } from 'lucide-react';
import omnichannelService from '../../services/omnichannelService';
import { useTranslation } from 'react-i18next';

interface OmnichannelStats {
  activeChannels: number;
  activeConversations: number;
  pendingConversations: number;
  assignedConversations: number;
  avgResponseTime: number;
  totalMessages: number;
}

export default function OmnichannelOverview() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState<OmnichannelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveChannels, setHasActiveChannels] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await omnichannelService.getDashboardStats();
        setStats(data);
        setHasActiveChannels(data.activeChannels > 0);
      } catch (error) {
        console.error('❌ Failed to fetch omnichannel stats:', error);
        setHasActiveChannels(false);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Don't show if no active channels
  if (!loading && !hasActiveChannels) {
    return null;
  }

  if (loading) {
    return (
      <div className={`rounded-lg shadow p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="animate-pulse space-y-4">
          <div className={`h-6 w-48 rounded ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          }`}></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`h-20 rounded ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const formatResponseTime = (minutes: number) => {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className={`rounded-lg shadow p-6 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {t('atrium.overview')}
          </h3>
          <p className={`text-sm mt-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('atrium.activeChannels', { count: stats.activeChannels })} • {t('atrium.realtimeStats')}
          </p>
        </div>
        <button
          onClick={() => navigate('/settings/omnichannel')}
          className={`p-2 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
              : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
          }`}
          title={t('atrium.settings')}
        >
          <Settings className="h-5 w-5" />
        </button>
      </div>

      {/* Stats Grid */}
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Active Conversations */}
          <div
            onClick={() => navigate('/omnichannel')}
            className={`p-4 rounded-lg cursor-pointer transition-all ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className={`h-4 w-4 ${
                theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
              }`} />
              <span className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Active
              </span>
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {stats.activeConversations}
            </div>
            <div className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              conversations
            </div>
          </div>

          {/* Pending */}
          <div
            onClick={() => navigate('/omnichannel')}
            className={`p-4 rounded-lg cursor-pointer transition-all ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className={`h-4 w-4 ${
                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
              <span className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Pending
              </span>
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {stats.pendingConversations}
            </div>
            <div className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              unassigned
            </div>
          </div>

          {/* Assigned */}
          <div
            onClick={() => navigate('/omnichannel')}
            className={`p-4 rounded-lg cursor-pointer transition-all ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className={`h-4 w-4 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`} />
              <span className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Assigned
              </span>
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {stats.assignedConversations}
            </div>
            <div className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              to agents
            </div>
          </div>

          {/* Avg Response Time */}
          <div className={`p-4 rounded-lg ${
            theme === 'dark'
              ? 'bg-gray-700'
              : 'bg-gray-50'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className={`h-4 w-4 ${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`} />
              <span className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Avg Response
              </span>
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {formatResponseTime(stats.avgResponseTime)}
            </div>
            <div className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              response time
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex gap-2">
          <button
            onClick={() => navigate('/omnichannel')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            View All Conversations
          </button>
        </div>
      </div>
    </div>
  );
}
